// Built-in stamp icon geometry — single source of truth for both renderers:
//   - apps/web wraps `content` in React JSX inside a shared <Glyph> wrapper.
//   - apps/api wraps `content` in an SVG string and pipes to sharp for the
//     wallet hero PNG composition.
//
// The wrapper attrs (fill / stroke / stroke-width / stroke-line*) are derived
// from `state` ('filled' | 'empty') by each renderer — identically. Per-element
// attribute overrides inside `content` (e.g. coffee's inner paths setting
// fill="none" and strokeWidth=1.6) work in both renderers because we keep the
// markup literal.
//
// To add an icon: add its id to BUILTIN_STAMP_ICONS in constants, then add the
// entry here. Don't add it just on the web side.
import { BUILTIN_STAMP_ICONS } from './constants/index.js';
/**
 * Parse a `builtin:<id>:<state>` ref. Returns null for custom-URL refs
 * (those are image URLs that callers should treat as raster sources).
 * Used by both the web renderer and the API hero generator to discriminate
 * built-in vs custom stamp icons.
 */
export function parseBuiltinRef(ref) {
    const m = /^builtin:([a-z]+):(filled|empty)$/.exec(ref);
    if (!m)
        return null;
    const id = m[1];
    if (!BUILTIN_STAMP_ICONS.includes(id))
        return null;
    return { id, state: m[2] };
}
export const BUILTIN_STAMP_ICON_GEOMETRY = {
    circle: {
        viewBox: '0 0 24 24',
        content: `<circle cx="12" cy="12" r="9" />`,
    },
    square: {
        viewBox: '0 0 24 24',
        content: `<rect x="3" y="3" width="18" height="18" rx="3" ry="3" />`,
    },
    star: {
        viewBox: '0 0 24 24',
        content: `<path d="M12 2.5l2.9 5.9 6.5.95-4.7 4.58 1.11 6.47L12 17.9l-5.81 3.06 1.11-6.47L2.6 9.9l6.5-.95L12 2.5z" />`,
    },
    heart: {
        viewBox: '0 0 24 24',
        // Symmetric heart about x=12 — four cubic Béziers (two per side) meeting
        // at a center-top dip to form the cusp.
        content: `<path d="M 12 21 C 2 16, 1 6, 6 4 C 9 3, 11 5, 12 8 C 13 5, 15 3, 18 4 C 23 6, 22 16, 12 21 Z" />`,
    },
    coffee: {
        viewBox: '0 0 24 24',
        // Three paths: cup body (uses wrapper fill/stroke), handle (always stroke
        // only, fill="none"), steam wisps (always stroke only, finer width).
        content: `<path d="M4 8h12v6a5 5 0 0 1-5 5H9a5 5 0 0 1-5-5V8z" /><path d="M16 9h2.5a2.5 2.5 0 0 1 0 5H16" fill="none" stroke-width="1.8" /><path d="M7 2.5c-.6 1-.6 2 0 3M11 2.5c-.6 1-.6 2 0 3" fill="none" stroke-width="1.6" />`,
    },
    paw: {
        viewBox: '0 0 24 24',
        content: `<ellipse cx="12" cy="15.5" rx="4.5" ry="3.6" /><circle cx="6.5" cy="11" r="2.1" /><circle cx="17.5" cy="11" r="2.1" /><circle cx="9.2" cy="6.8" r="1.9" /><circle cx="14.8" cy="6.8" r="1.9" />`,
    },
};
/**
 * Build a complete SVG string for a built-in icon in a given state. Both the
 * web and API renderers can use this directly (web feeds it to dangerouslySet
 * via dangerouslySetInnerHTML or builds the equivalent JSX; API pipes it to
 * sharp). Returns null for unknown ids.
 */
export function buildBuiltinStampSvg(id, state, color) {
    const g = BUILTIN_STAMP_ICON_GEOMETRY[id];
    const solid = state === 'filled';
    const fill = solid ? color : 'none';
    const stroke = color;
    const strokeWidth = solid ? 0 : 1.8;
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${g.viewBox}" fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}" stroke-linejoin="round" stroke-linecap="round">${g.content}</svg>`;
}
//# sourceMappingURL=stamp-icons.js.map